/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: AzureVoiceCascadingSelection.tsx
 🗓️ Created: 2026-01-26
 🧩 Description:
    Voice selection component for Azure TTS with cascading selectors
    matching the Google TTS experience: Language → Accent → Voice → Style

 🔧 Notes:
    - Uses cascading selection: Language → Locale → Voice
    - Style selector for voices with StyleList
    - Audio controls shown after complete selection

 🔁 Last Updated: 2026-01-26 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Volume2, RotateCcw, Globe, MapPin, Mic, Music, Loader2, Play, Download } from 'lucide-react';
import { Label } from '../../../../components/ui/label';
import { Button } from '../../../../components/ui/button';
import Select from '../../../../components/ui/select';
import Combobox from '../../../../components/ui/Combobox';
import { FormBox } from '../../../../components/ui/FormBox';
import { useI18n } from '../../../../hooks/useI18n';
import { AiProfile, AzureTTSVoice, VoiceStyles } from '../../types';
import { api } from '../../../../lib/api';
import {
    getLanguageFromLocale,
    getRegionFromLocale,
    extractUniqueLanguages,
    extractLocalesForLanguage,
    extractVoicesForLocale,
} from './azureVoiceData';

interface AzureVoiceCascadingSelectionProps {
    formData: AiProfile;
    availableVoicesData: AzureTTSVoice[];
    loadingVoices: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onInputChange: (field: string, value: any) => void;
    selectedVoiceData: AzureTTSVoice | null;
    onVoiceSelect: (voice: AzureTTSVoice | null) => void;
    voiceStyles: VoiceStyles;
    onVoiceStyleSet: (field: string, value: string) => void;
}

// Azure voice config interface
interface AzureVoiceConfig {
    language: string;
    locale: string;
    voice: string;
    voiceShortName: string;
    gender: string;
    style: string;
    pitch: string;
    volume: string;
    rate: string;
}

export default function AzureVoiceCascadingSelection({
    formData,
    availableVoicesData,
    loadingVoices,
    onInputChange,
    selectedVoiceData,
    onVoiceSelect,
    voiceStyles,
    onVoiceStyleSet
}: AzureVoiceCascadingSelectionProps) {
    const { t } = useI18n();

    // Parse existing config or create default
    const [voiceConfig, setVoiceConfig] = useState<AzureVoiceConfig>(() => {
        try {
            if (formData.voice_config) {
                const parsed = typeof formData.voice_config === 'string'
                    ? JSON.parse(formData.voice_config)
                    : formData.voice_config;

                // Derive language from locale if not directly stored
                let language = parsed.language || '';
                if (!language && parsed.locale) {
                    language = getLanguageFromLocale(parsed.locale);
                }

                return {
                    language: language,
                    locale: parsed.locale || '',
                    voice: parsed.voice || parsed.name || '',
                    voiceShortName: parsed.voiceShortName || parsed.name || '',
                    gender: parsed.gender || '',
                    style: parsed.style || parsed.styles?.style || '',
                    pitch: parsed.pitch || parsed.audioConfig?.pitch || '0%',
                    volume: parsed.volume || parsed.audioConfig?.volume || '100%',
                    rate: parsed.rate || parsed.audioConfig?.rate || '1x'
                };
            }
        } catch (error) {
            console.error('Error parsing voice_config:', error);
        }
        return {
            language: '',
            locale: '',
            voice: '',
            voiceShortName: '',
            gender: '',
            style: '',
            pitch: '0%',
            volume: '100%',
            rate: '1x'
        };
    });

    // Track which voice we've initialized from to avoid unnecessary re-initialization
    const initializedVoiceKeyRef = useRef<string | null>(null);

    // Sync AUDIO SETTINGS (rate, pitch, volume) immediately when formData changes
    // This does NOT need to wait for availableVoicesData
    useEffect(() => {
        if (!formData.voice_config) {
            return;
        }

        try {
            const parsed = typeof formData.voice_config === 'string'
                ? JSON.parse(formData.voice_config)
                : formData.voice_config;

            const newRate = parsed.rate || parsed.audioConfig?.rate || '1x';
            const newPitch = parsed.pitch || parsed.audioConfig?.pitch || '0%';
            const newVolume = parsed.volume || parsed.audioConfig?.volume || '100%';

            // Always sync audio settings from formData
            setVoiceConfig(prev => {
                // Only update if values actually changed
                if (prev.rate !== newRate || prev.pitch !== newPitch || prev.volume !== newVolume) {
                    return {
                        ...prev,
                        pitch: newPitch,
                        volume: newVolume,
                        rate: newRate
                    };
                }
                return prev;
            });
        } catch (error) {
            console.error('Error syncing audio settings:', error);
        }
    }, [formData.voice_config]);

    // Sync VOICE SELECTION when formData changes AND voices are loaded
    // This needs availableVoicesData to populate the selectors
    useEffect(() => {
        if (!formData.voice_config || availableVoicesData.length === 0) {
            return;
        }

        try {
            const parsed = typeof formData.voice_config === 'string'
                ? JSON.parse(formData.voice_config)
                : formData.voice_config;

            // Create a unique key based on the actual voice identity
            const voiceKey = `${parsed.locale || ''}|${parsed.name || parsed.voice || ''}`;

            // Only re-initialize voice selection if voice changed
            if ((parsed.locale || parsed.name) && initializedVoiceKeyRef.current !== voiceKey) {
                let language = parsed.language || '';
                if (!language && parsed.locale) {
                    language = getLanguageFromLocale(parsed.locale);
                }

                setVoiceConfig(prev => ({
                    ...prev,
                    language: language,
                    locale: parsed.locale || '',
                    voice: parsed.voice || parsed.name || '',
                    voiceShortName: parsed.voiceShortName || parsed.name || '',
                    gender: parsed.gender || '',
                    style: parsed.style || parsed.styles?.style || ''
                }));

                initializedVoiceKeyRef.current = voiceKey;
            }
        } catch (error) {
            console.error('Error syncing voice selection:', error);
        }
    }, [formData.voice_config, availableVoicesData]);

    // Initialize from existing selectedVoiceData
    useEffect(() => {
        if (selectedVoiceData && !voiceConfig.language) {
            const lang = getLanguageFromLocale(selectedVoiceData.Locale);
            setVoiceConfig(prev => ({
                ...prev,
                language: lang,
                locale: selectedVoiceData.Locale,
                voice: selectedVoiceData.ShortName,
                voiceShortName: selectedVoiceData.ShortName,
                gender: selectedVoiceData.Gender
            }));
        }
    }, [selectedVoiceData]);

    // Derived lists based on selections
    const languages = useMemo(() => extractUniqueLanguages(availableVoicesData), [availableVoicesData]);
    const locales = useMemo(() =>
        voiceConfig.language ? extractLocalesForLanguage(availableVoicesData, voiceConfig.language) : [],
        [availableVoicesData, voiceConfig.language]
    );
    const voiceOptions = useMemo(() =>
        voiceConfig.language && voiceConfig.locale
            ? extractVoicesForLocale(availableVoicesData, voiceConfig.language, voiceConfig.locale)
            : [],
        [availableVoicesData, voiceConfig.language, voiceConfig.locale]
    );

    // Get available styles for selected voice
    const availableStyles = useMemo(() => {
        if (!selectedVoiceData?.StyleList || selectedVoiceData.StyleList.length === 0) {
            return [];
        }
        return selectedVoiceData.StyleList.map(style => ({
            value: style,
            label: style.replace(/([A-Z])/g, ' $1').trim()
        }));
    }, [selectedVoiceData]);

    // Extract audio values directly from formData.voice_config for slider display
    // This is the source of truth for display, avoiding sync issues with local voiceConfig state
    const audioValues = useMemo(() => {
        try {
            if (formData.voice_config) {
                const parsed = typeof formData.voice_config === 'string'
                    ? JSON.parse(formData.voice_config)
                    : formData.voice_config;
                return {
                    rate: parsed.rate || parsed.audioConfig?.rate || '1x',
                    pitch: parsed.pitch || parsed.audioConfig?.pitch || '0%',
                    volume: parsed.volume || parsed.audioConfig?.volume || '100%'
                };
            }
        } catch (error) {
            console.error('Error parsing voice_config for audio values:', error);
        }
        return { rate: '1x', pitch: '0%', volume: '100%' };
    }, [formData.voice_config]);

    // Check if voice selection is complete
    const isVoiceSelected = voiceConfig.language && voiceConfig.locale && voiceConfig.voice;

    // Audio generation state
    const [testText, setTestText] = useState('');
    const [isGeneratingAudio, setIsGeneratingAudio] = useState(false);
    const [audioUrl, setAudioUrl] = useState<string | null>(null);
    const [audioError, setAudioError] = useState<string | null>(null);
    const audioRef = useRef<HTMLAudioElement>(null);

    // Play the generated audio
    const handlePlayAudio = useCallback(() => {
        if (audioRef.current && audioUrl) {
            audioRef.current.play();
        }
    }, [audioUrl]);

    // Handle inline TTS generation
    const handleTestVoice = useCallback(async () => {
        if (!testText.trim()) {
            setAudioError(t('aiProfiles.voiceConfig.enterTextPlaceholder') || 'Enter text to test');
            return;
        }

        if (!selectedVoiceData) {
            setAudioError(t('aiProfiles.errors.selectVoiceFirst') || 'Select a voice first');
            return;
        }

        try {
            if (audioUrl) {
                URL.revokeObjectURL(audioUrl);
                setAudioUrl(null);
            }
            setIsGeneratingAudio(true);
            setAudioError(null);

            const tenantSlug = localStorage.getItem('tenant-slug') || 'main';
            const baseUrl = api.defaults.baseURL || '';

            // Build SSML for Azure TTS
            const voiceName = selectedVoiceData.Name || selectedVoiceData.ShortName;

            // Convert rate to Azure SSML format: "x-slow", "slow", "medium", "fast", "x-fast", or percentage like "+20%"
            let ssmlRate = voiceConfig.rate || 'medium';
            if (typeof ssmlRate === 'string' && ssmlRate.match(/^[\d.]+x?$/)) {
                const numericRate = parseFloat(ssmlRate.replace('x', ''));
                if (!isNaN(numericRate) && numericRate !== 1.0) {
                    // Convert to percentage: 1.2 -> "+20%", 0.8 -> "-20%"
                    const percentChange = Math.round((numericRate - 1) * 100);
                    ssmlRate = percentChange >= 0 ? `+${percentChange}%` : `${percentChange}%`;
                } else {
                    ssmlRate = 'medium'; // 1.0 = default rate
                }
            }

            const ssmlContent = `<speak xmlns="http://www.w3.org/2001/10/synthesis" version="1.0" xml:lang="${selectedVoiceData.Locale}">
<voice name="${voiceName}">
<prosody pitch="${voiceConfig.pitch}" volume="${voiceConfig.volume}" rate="${ssmlRate}">
${testText.trim()}
</prosody>
</voice>
</speak>`;

            const response = await fetch(`${baseUrl}/api/ai/profiles/test-tts`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Tenant-Slug': tenantSlug
                },
                credentials: 'include',
                body: JSON.stringify({
                    provider_id: formData.provider_id,
                    ssml: ssmlContent,
                    output_format: 'audio-24khz-48kbitrate-mono-mp3'
                })
            });

            if (response.ok) {
                const audioBlob = await response.blob();
                const url = URL.createObjectURL(audioBlob);
                setAudioUrl(url);
            } else {
                const errorData = await response.json().catch(() => ({ message: 'Error generating audio' }));
                console.error('TTS API error:', errorData);
                setAudioError(errorData.message || t('aiProfiles.errors.ttsFailed') || 'Error generating audio');
            }
        } catch (error: any) {
            console.error('TTS fetch error:', error);
            setAudioError(error.message || t('aiProfiles.errors.ttsFailed') || 'Connection error');
        } finally {
            setIsGeneratingAudio(false);
        }
    }, [testText, voiceConfig, selectedVoiceData, formData.provider_id, audioUrl, t]);

    // Update config and propagate to form
    const updateConfig = (updates: Partial<AzureVoiceConfig>) => {
        // Parse formData.voice_config to get complete saved state
        let baseConfig: any = {};
        try {
            if (formData.voice_config) {
                baseConfig = typeof formData.voice_config === 'string'
                    ? JSON.parse(formData.voice_config)
                    : { ...formData.voice_config };
            }
        } catch {
            baseConfig = {};
        }

        // Remove legacy audioConfig wrapper
        if (baseConfig.audioConfig) {
            delete baseConfig.audioConfig;
        }

        // Merge with voiceConfig state for local values, then apply updates
        const newConfig = { ...baseConfig, ...voiceConfig, ...updates };
        setVoiceConfig(newConfig);
        onInputChange('voice_config', JSON.stringify(newConfig));

        // Also update the model field with the voice short name for backend compatibility
        if (updates.voiceShortName) {
            onInputChange('voice_model', updates.voiceShortName);
            onInputChange('model', updates.voiceShortName);
        }
    };

    // Update audio settings (pitch, volume, rate) by parsing formData.voice_config as base
    // This ensures audio updates don't lose other saved values when voiceConfig state is stale
    const updateAudioConfig = (audioUpdates: Partial<Pick<AzureVoiceConfig, 'pitch' | 'volume' | 'rate'>>) => {
        let baseConfig: any = {};
        try {
            if (formData.voice_config) {
                baseConfig = typeof formData.voice_config === 'string'
                    ? JSON.parse(formData.voice_config)
                    : { ...formData.voice_config };
            }
        } catch {
            baseConfig = {};
        }

        // Remove legacy audioConfig wrapper to prevent interference with top-level values
        if (baseConfig.audioConfig) {
            delete baseConfig.audioConfig;
        }

        // Merge audio updates with existing config (now only top-level rate/pitch/volume)
        const newConfig = { ...baseConfig, ...audioUpdates };

        // Also update local state for voice selection to work correctly
        setVoiceConfig(prev => ({ ...prev, ...audioUpdates }));

        onInputChange('voice_config', JSON.stringify(newConfig));
    };

    // Handle language change - reset locale and voice
    const handleLanguageChange = (lang: string) => {
        updateConfig({
            language: lang,
            locale: '',
            voice: '',
            voiceShortName: '',
            gender: '',
            style: ''
        });
        onVoiceSelect(null);
    };

    // Handle locale change - reset voice
    const handleLocaleChange = (loc: string) => {
        updateConfig({
            locale: loc,
            voice: '',
            voiceShortName: '',
            gender: '',
            style: ''
        });
        onVoiceSelect(null);
    };

    // Handle voice selection
    const handleVoiceChange = (shortName: string) => {
        const selectedVoice = voiceOptions.find(v => v.ShortName === shortName);
        if (selectedVoice) {
            updateConfig({
                voice: shortName,
                voiceShortName: selectedVoice.ShortName,
                gender: selectedVoice.Gender,
                style: ''
            });
            onVoiceSelect(selectedVoice);
        }
    };

    // Handle style selection
    const handleStyleChange = (style: string) => {
        updateConfig({ style });
        onVoiceStyleSet('style', style);
    };

    // Prepare language options for Combobox
    const languageOptions = useMemo(() =>
        languages.map(lang => ({
            value: lang,
            label: lang,
            checked: voiceConfig.language === lang
        })),
        [languages, voiceConfig.language]
    );

    if (loadingVoices) {
        return (
            <FormBox title={t('aiProfiles.sections.voiceSelection') || 'Voice Selection'} icon={<Volume2 />}>
                <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-primary mr-2" />
                    <span className="text-muted-foreground">{t('aiProfiles.modals.loadingModels') || 'Loading voices...'}</span>
                </div>
            </FormBox>
        );
    }

    return (
        <FormBox title={t('aiProfiles.sections.voiceSelection') || 'Voice Selection'} icon={<Volume2 />}>
            <div className="space-y-6">
                {/* Cascading Selectors */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {/* Language Selector with Search */}
                    <div>
                        <Label className="flex items-center gap-2 mb-2">
                            <Globe className="h-4 w-4" />
                            {t('aiProfiles.azureTts.language') || t('aiProfiles.googleTts.language') || 'Language'}
                        </Label>
                        <Combobox
                            single
                            options={languageOptions}
                            onToggle={handleLanguageChange}
                            placeholder={t('aiProfiles.azureTts.selectLanguage') || t('aiProfiles.googleTts.selectLanguage') || 'Select language'}
                            translations={{
                                searchOptions: t('common.search') || 'Search...',
                                noOptionsFound: t('common.noResults') || 'Not found'
                            }}
                        />
                    </div>

                    {/* Locale/Accent Selector */}
                    <div>
                        <Label className="flex items-center gap-2 mb-2">
                            <MapPin className="h-4 w-4" />
                            {t('aiProfiles.azureTts.locale') || t('aiProfiles.googleTts.accent') || 'Accent'}
                        </Label>
                        <Select
                            value={voiceConfig.locale}
                            onValueChange={handleLocaleChange}
                            options={locales}
                            placeholder={t('aiProfiles.azureTts.selectLocale') || t('aiProfiles.googleTts.selectAccent') || 'Select accent'}
                            disabled={!voiceConfig.language || locales.length === 0}
                        />
                    </div>

                    {/* Voice Selector */}
                    <div>
                        <Label className="flex items-center gap-2 mb-2">
                            <Mic className="h-4 w-4" />
                            {t('aiProfiles.azureTts.voice') || t('aiProfiles.googleTts.voiceStyle') || 'Voice'}
                        </Label>
                        {voiceConfig.locale && voiceOptions.length > 0 ? (
                            <Combobox
                                single
                                options={voiceOptions.map(v => ({
                                    value: v.ShortName,
                                    label: `${v.ShortName} (${v.Gender})`,
                                    checked: voiceConfig.voice === v.ShortName
                                }))}
                                onToggle={handleVoiceChange}
                                placeholder={t('aiProfiles.azureTts.selectVoice') || t('aiProfiles.googleTts.selectVoice') || 'Select voice'}
                                translations={{
                                    searchOptions: t('common.search') || 'Search...',
                                    noOptionsFound: t('common.noResults') || 'Not found'
                                }}
                            />
                        ) : (
                            <Select
                                value=""
                                onValueChange={() => { }}
                                options={[]}
                                placeholder={t('aiProfiles.azureTts.selectVoice') || t('aiProfiles.googleTts.selectVoice') || 'Select voice'}
                                disabled={true}
                            />
                        )}
                    </div>

                    {/* Style Selector */}
                    <div>
                        <Label className="flex items-center gap-2 mb-2">
                            <Music className="h-4 w-4" />
                            {t('aiProfiles.azureTts.style') || t('aiProfiles.googleTts.styleLabel') || 'Style'}
                        </Label>
                        <Select
                            value={voiceConfig.style}
                            onValueChange={handleStyleChange}
                            options={[
                                { value: '', label: t('aiProfiles.voiceConfig.default') || 'Default' },
                                ...availableStyles
                            ]}
                            placeholder={t('aiProfiles.azureTts.selectStyle') || t('aiProfiles.googleTts.selectStyle') || 'Select style'}
                            disabled={!voiceConfig.voice || availableStyles.length === 0}
                        />
                    </div>
                </div>

                {/* Selected Voice Info - Show after selection */}
                {isVoiceSelected && selectedVoiceData && (
                    <>
                        {/* Hidden Audio Element for playback */}
                        <audio
                            ref={audioRef}
                            src={audioUrl || undefined}
                            autoPlay
                            style={{ display: 'none' }}
                        />

                        {/* Audio Controls */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {/* Pitch Slider */}
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <Label className="flex items-center gap-2">
                                        <span>🎵</span> {t('aiProfiles.voiceConfig.pitch') || 'Pitch'}: {audioValues.pitch}
                                    </Label>
                                    {audioValues.pitch !== '0%' && (
                                        <button
                                            type="button"
                                            onClick={() => updateAudioConfig({ pitch: '0%' })}
                                            className="p-1 rounded transition-colors text-orange-500 hover:text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-950"
                                            title="Reset"
                                        >
                                            <RotateCcw className="h-3 w-3" />
                                        </button>
                                    )}
                                </div>
                                <input
                                    type="range"
                                    min="-50"
                                    max="50"
                                    step="1"
                                    value={parseInt(audioValues.pitch) || 0}
                                    onChange={(e) => updateAudioConfig({ pitch: `${parseInt(e.target.value)}%` })}
                                    className="w-full h-2 bg-secondary rounded-lg appearance-none cursor-pointer accent-primary"
                                />
                                <div className="flex justify-between text-xs text-muted-foreground">
                                    <span>{t('aiProfiles.googleTts.low') || 'Low'}</span>
                                    <span>{t('aiProfiles.googleTts.high') || 'High'}</span>
                                </div>
                            </div>

                            {/* Volume Slider */}
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <Label className="flex items-center gap-2">
                                        <span>🔊</span> {t('aiProfiles.voiceConfig.volume') || 'Volume'}: {audioValues.volume}
                                    </Label>
                                    {audioValues.volume !== '100%' && (
                                        <button
                                            type="button"
                                            onClick={() => updateAudioConfig({ volume: '100%' })}
                                            className="p-1 rounded transition-colors text-orange-500 hover:text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-950"
                                            title="Reset"
                                        >
                                            <RotateCcw className="h-3 w-3" />
                                        </button>
                                    )}
                                </div>
                                <input
                                    type="range"
                                    min="0"
                                    max="200"
                                    step="5"
                                    value={parseInt(audioValues.volume) || 100}
                                    onChange={(e) => updateAudioConfig({ volume: `${parseInt(e.target.value)}%` })}
                                    className="w-full h-2 bg-secondary rounded-lg appearance-none cursor-pointer accent-primary"
                                />
                                <div className="flex justify-between text-xs text-muted-foreground">
                                    <span>{t('aiProfiles.googleTts.quiet') || 'Quiet'}</span>
                                    <span>{t('aiProfiles.googleTts.loud') || 'Loud'}</span>
                                </div>
                            </div>

                            {/* Rate Slider */}
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <Label className="flex items-center gap-2">
                                        <span>⚡</span> {t('aiProfiles.googleTts.speed') || 'Speed'}: {audioValues.rate}
                                    </Label>
                                    {audioValues.rate !== '1x' && (
                                        <button
                                            type="button"
                                            onClick={() => updateAudioConfig({ rate: '1x' })}
                                            className="p-1 rounded transition-colors text-orange-500 hover:text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-950"
                                            title="Reset"
                                        >
                                            <RotateCcw className="h-3 w-3" />
                                        </button>
                                    )}
                                </div>
                                <input
                                    type="range"
                                    min="0.5"
                                    max="2"
                                    step="0.1"
                                    value={parseFloat(audioValues.rate) || 1}
                                    onChange={(e) => updateAudioConfig({ rate: `${parseFloat(e.target.value)}x` })}
                                    className="w-full h-2 bg-secondary rounded-lg appearance-none cursor-pointer accent-primary"
                                />
                                <div className="flex justify-between text-xs text-muted-foreground">
                                    <span>{t('aiProfiles.googleTts.slow') || 'Slow'}</span>
                                    <span>{t('aiProfiles.googleTts.fast') || 'Fast'}</span>
                                </div>
                            </div>
                        </div>

                        {/* Test Text Area with inline buttons */}
                        <div>
                            <Label className="text-sm font-medium text-foreground mb-2 block">
                                {t('aiProfiles.voiceConfig.textToConvert') || 'Text to convert:'}
                            </Label>
                            <textarea
                                value={testText}
                                onChange={(e) => setTestText(e.target.value)}
                                placeholder={t('aiProfiles.voiceConfig.enterTextPlaceholder') || 'Enter text to convert to speech...'}
                                className="w-full px-3 py-2 text-sm bg-card border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-y text-foreground"
                                rows={2}
                            />
                        </div>

                        {/* Error Display */}
                        {audioError && !isGeneratingAudio && (
                            <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-3">
                                <p className="text-sm text-red-600 dark:text-red-400">{audioError}</p>
                            </div>
                        )}

                        {/* Audio Actions Container */}
                        <div className="flex items-center gap-2 p-3 bg-primary/5 border border-primary/20 rounded-lg">
                            <Button
                                type="button"
                                onClick={handleTestVoice}
                                disabled={isGeneratingAudio || !testText.trim()}
                                variant="default"
                                className="flex-1"
                            >
                                {isGeneratingAudio ? (
                                    <>
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        {t('aiProfiles.googleTts.generatingAudio') || 'Generating...'}
                                    </>
                                ) : (
                                    <>
                                        <Volume2 className="h-4 w-4 mr-2" />
                                        {t('aiProfiles.googleTts.convertToAudio') || 'Convert to Audio'}
                                    </>
                                )}
                            </Button>
                            <Button
                                type="button"
                                onClick={handlePlayAudio}
                                disabled={!audioUrl || isGeneratingAudio}
                                variant="outline"
                                className="border-green-500 text-green-600 hover:bg-green-500/10 hover:text-green-600"
                            >
                                <Play className="h-4 w-4 mr-2" />
                                {t('aiProfiles.googleTts.playAudio') || 'Play'}
                            </Button>
                            <Button
                                type="button"
                                onClick={() => {
                                    if (audioUrl) {
                                        const link = document.createElement('a');
                                        link.href = audioUrl;
                                        link.download = `voice-test-${selectedVoiceData.ShortName}.mp3`;
                                        document.body.appendChild(link);
                                        link.click();
                                        document.body.removeChild(link);
                                    }
                                }}
                                disabled={!audioUrl || isGeneratingAudio}
                                variant="outline"
                                className="border-cyan-500 text-cyan-600 hover:bg-cyan-500/10 hover:text-cyan-600"
                            >
                                <Download className="h-4 w-4 mr-2" />
                                {t('aiProfiles.googleTts.downloadAudio') || 'Download'}
                            </Button>
                        </div>
                    </>
                )}

                {/* Hint when no voice selected */}
                {!isVoiceSelected && (
                    <p className="text-sm text-muted-foreground text-center py-4">
                        {t('aiProfiles.azureTts.selectVoiceHint') || t('aiProfiles.googleTts.selectVoiceHint') || 'Select language, accent, and voice to see audio options'}
                    </p>
                )}
            </div>
        </FormBox>
    );
}
